import React, { useState } from 'react';
import '../components-styles/player-submission-form.css';

function PlayerSubmissionForm() {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    nationality: '',
    position: '',
    current_club: '',
    height_cm: '',
    weight_kg: '',
    dominant_foot: '',
    playing_style: '',
    achievements: '',
    email: '',
    phone: '',
    has_transfer_link: 'no',
    transfer_link: '',
    has_highlight_videos: 'no',
    highlight_videos: [],
    has_full_highlight_video: 'no'
  });

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleFileChange = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => {
        const newVideos = [...prev.highlight_videos];
        newVideos[index] = file;
        return {
          ...prev,
          highlight_videos: newVideos
        };
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      // Validate highlight videos if selected
      if (formData.has_highlight_videos === 'yes' && formData.highlight_videos.filter(v => v).length === 0) {
        throw new Error('Please upload at least one highlight video');
      }

      const formDataToSend = new FormData();
      
      // Add all text fields
      formDataToSend.append('first_name', formData.first_name);
      formDataToSend.append('last_name', formData.last_name);
      formDataToSend.append('date_of_birth', formData.date_of_birth);
      formDataToSend.append('nationality', formData.nationality);
      formDataToSend.append('position', formData.position);
      formDataToSend.append('current_club', formData.current_club);
      formDataToSend.append('height_cm', formData.height_cm);
      formDataToSend.append('weight_kg', formData.weight_kg);
      formDataToSend.append('dominant_foot', formData.dominant_foot);
      formDataToSend.append('playing_style', formData.playing_style);
      formDataToSend.append('achievements', formData.achievements);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      
      // Add new fields
      formDataToSend.append('has_transfer_link', formData.has_transfer_link);
      formDataToSend.append('transfer_link', formData.transfer_link);
      formDataToSend.append('has_highlight_videos', formData.has_highlight_videos);
      formDataToSend.append('has_full_highlight_video', formData.has_full_highlight_video);
      
      // Add highlight video files
      formData.highlight_videos.forEach((video, index) => {
        if (video) {
          formDataToSend.append(`highlight_videos`, video);
        }
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/player-submissions/`, {
        method: 'POST',
        body: formDataToSend
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || JSON.stringify(errorData) || 'Failed to submit form');
      }

      setSubmitted(true);
      setFormData({
        first_name: '',
        last_name: '',
        date_of_birth: '',
        nationality: '',
        position: '',
        current_club: '',
        height_cm: '',
        weight_kg: '',
        dominant_foot: '',
        playing_style: '',
        achievements: '',
        email: '',
        phone: '',
        has_transfer_link: 'no',
        transfer_link: '',
        has_highlight_videos: 'no',
        highlight_videos: [],
        has_full_highlight_video: 'no'
      });
      
      setTimeout(() => {
        setSubmitted(false);
      }, 5000);
    } catch (err) {
      setError(err.message || 'An error occurred while submitting the form');
      console.error('Submission error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="player-submission-form" onSubmit={handleSubmit}>
      {submitted && <div className="success-message">✓ Profile submitted successfully! We'll review your information and get back to you soon.</div>}
      {error && <div className="error-message">✗ Error: {error}</div>}

      <div className="form-group">
        <label htmlFor="first_name">First Name *</label>
        <input
          type="text"
          id="first_name"
          name="first_name"
          value={formData.first_name}
          onChange={handleChange}
          required
          placeholder="Your first name"
        />
      </div>

      <div className="form-group">
        <label htmlFor="last_name">Last Name *</label>
        <input
          type="text"
          id="last_name"
          name="last_name"
          value={formData.last_name}
          onChange={handleChange}
          required
          placeholder="Your last name"
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="date_of_birth">Date of Birth *</label>
          <input
            type="date"
            id="date_of_birth"
            name="date_of_birth"
            value={formData.date_of_birth}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="nationality">Nationality / Passport *</label>
          <input
            type="text"
            id="nationality"
            name="nationality"
            value={formData.nationality}
            onChange={handleChange}
            required
            placeholder="e.g., England, Spain"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="position">Position(s) *</label>
          <input
            type="text"
            id="position"
            name="position"
            value={formData.position}
            onChange={handleChange}
            required
            placeholder="e.g., Striker, Midfielder"
          />
        </div>
        <div className="form-group">
          <label htmlFor="current_club">Current Club / Status *</label>
          <input
            type="text"
            id="current_club"
            name="current_club"
            value={formData.current_club}
            onChange={handleChange}
            required
            placeholder="e.g., Club Name or Free Agent"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="height_cm">Height (cm)</label>
          <input
            type="number"
            id="height_cm"
            name="height_cm"
            value={formData.height_cm}
            onChange={handleChange}
            placeholder="e.g., 180"
          />
        </div>
        <div className="form-group">
          <label htmlFor="weight_kg">Weight (kg)</label>
          <input
            type="number"
            id="weight_kg"
            name="weight_kg"
            value={formData.weight_kg}
            onChange={handleChange}
            placeholder="e.g., 75"
          />
        </div>
        <div className="form-group">
          <label htmlFor="dominant_foot">Preferred Foot</label>
          <select
            id="dominant_foot"
            name="dominant_foot"
            value={formData.dominant_foot}
            onChange={handleChange}
          >
            <option value="">Select...</option>
            <option value="Left">Left</option>
            <option value="Right">Right</option>
            <option value="Both">Both</option>
          </select>
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="playing_style">Playing Style & Description</label>
        <textarea
          id="playing_style"
          name="playing_style"
          value={formData.playing_style}
          onChange={handleChange}
          placeholder="Describe your playing style, strengths, and unique qualities..."
          rows="3"
        ></textarea>
      </div>

      <div className="form-group">
        <label htmlFor="achievements">Achievements & Honours</label>
        <textarea
          id="achievements"
          name="achievements"
          value={formData.achievements}
          onChange={handleChange}
          placeholder="List your achievements, awards, and career highlights..."
          rows="3"
        ></textarea>
      </div>

      <div className="form-group">
        <label htmlFor="has_transfer_link">Do you have a Transfer Market Link? *</label>
        <select
          id="has_transfer_link"
          name="has_transfer_link"
          value={formData.has_transfer_link}
          onChange={handleChange}
          required
        >
          <option value="no">No</option>
          <option value="yes">Yes</option>
        </select>
      </div>

      {formData.has_transfer_link === 'yes' && (
        <div className="form-group">
          <label htmlFor="transfer_link">Transfer Market Link *</label>
          <input
            type="url"
            id="transfer_link"
            name="transfer_link"
            value={formData.transfer_link}
            onChange={handleChange}
            placeholder="https://transfermarkt.com/..."
            required={formData.has_transfer_link === 'yes'}
          />
        </div>
      )}

      <div className="form-group">
        <label htmlFor="has_highlight_videos">Do you have Highlight Videos? *</label>
        <select
          id="has_highlight_videos"
          name="has_highlight_videos"
          value={formData.has_highlight_videos}
          onChange={handleChange}
          required
        >
          <option value="no">No</option>
          <option value="yes">Yes</option>
        </select>
      </div>

      {formData.has_highlight_videos === 'yes' && (
        <div className="form-group highlight-videos-section">
          <label>Upload up to 5 Highlight Videos (Team & National Level) *</label>
          <p className="form-helper-text">Upload videos of your best performances at team and national level</p>
          {[0, 1, 2, 3, 4].map((index) => (
            <div key={index} className="video-upload-group">
              <label htmlFor={`highlight_video_${index}`}>Video {index + 1}</label>
              <input
                type="file"
                id={`highlight_video_${index}`}
                accept="video/*"
                onChange={(e) => handleFileChange(e, index)}
                required={index === 0}
              />
              {formData.highlight_videos[index] && (
                <span className="file-name">✓ {formData.highlight_videos[index].name}</span>
              )}
              <span className="file-hint">{index === 0 ? '(Required at least one)' : '(Optional)'}</span>
            </div>
          ))}
        </div>
      )}

      <div className="form-group">
        <label htmlFor="has_full_highlight_video">Do you have a Full/Max Highlight Video? *</label>
        <select
          id="has_full_highlight_video"
          name="has_full_highlight_video"
          value={formData.has_full_highlight_video}
          onChange={handleChange}
          required
        >
          <option value="no">No</option>
          <option value="yes">Yes</option>
        </select>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="email">Email *</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="your@email.com"
          />
        </div>
        <div className="form-group">
          <label htmlFor="phone">Phone *</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            placeholder="+44..."
          />
        </div>
      </div>

      <button type="submit" className="submit-btn" disabled={submitting}>
        {submitting ? 'Submitting...' : 'Submit Profile'}
      </button>
    </form>
  );
}

export default PlayerSubmissionForm;
